import { NodeProps, useReactFlow } from "reactflow";
import { operationType, selectDataType } from "../../../../services/types";
import styled from "styled-components";
import React from "react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-python";
import 'prismjs/components/prism-clike';
// @ts-ignore
import 'prismjs/themes/prism.css';
import { updateNodeValue } from "../../../../services/method";
import { Select } from "../../../form/select";
import { SettingsBlock, settingsDataType } from "../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../expanded";

type CodeProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function Code({ id, data, setChildMinimize, setChildSettings }: CodeProps) {
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'JavaScript', refId: 'javascript', isActive: true },
      { id: 2, name: 'Python', refId: 'python' },
   ]);
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      setRequests((requests) =>
      requests.map((request) => ({...request, isActive: request.refId === (data.value?.request || requests[0].refId) })));
      if (!data.value?.request) updateNodeValue(id, setNodes, { request: requests[0].refId, code: getExampleCode('javascript') });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleSelectRequest = (selectId: number) => {
      setRequests((requests) => requests.map((request) => {
         if (request.id === selectId) return { ...request, isActive: true };
         else return { ...request, isActive: false };
      }));
      const refId = requests.find((obj) => obj.id === selectId)?.refId;
      updateNodeValue(id, setNodes, { request: refId, code: getExampleCode(refId as any) });
   };

   // ---
   const handleChangeInput = (value: string) => { updateNodeValue(id, setNodes, { code: value }) };
   const highlightCode = (code: string) => Prism.highlight(code, Prism.languages[data.value?.request || 'plaintext'], data.value?.request || 'plaintext');
   // const highlightCode = (code: string) => Prism.highlight(code, Prism.languages.plaintext, 'plaintext');
   const numberOfLines = (data.value?.code || '').split("\n").length;
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
                <div id="warning"></div>
                <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            style={{ width: '420px' }}
         ><Styled>
            <Select title="Language" data={ requests } handleSelectItem={ handleSelectRequest} isSmall={ true } />
            <div className="code-wrapper">
               <div className="line-numbers">
                  { Array.from({ length: numberOfLines }, (_, i) => (<div key={i}>{i + 1}</div>)) }
               </div>
               <Editor
                  className="editor nodrag"
                  value={data.value?.code || ''}
                  onValueChange={(newCode) => handleChangeInput(newCode)}
                  highlight={ highlightCode }
                  padding={ 10 }
                  insertSpaces={ false }
                  style={{
                     fontFamily: '"Fira code", "Fira Mono", monospace', fontSize: 15,
                     lineHeight: 1.5, minHeight: '160px', flex: 1,
                  }}
               />
            </div>
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}

// ---
const Main = styled.div`
   width: 150px;
   padding: 0;
`;

// ---
export const Styled = styled.div`
   .select {
      margin-bottom: 10px;
   }
   .code-wrapper {
      display: flex;
      border: 1px dashed rgb(200, 200, 200);
      border-radius: 5px;
      background-color: #f6f6f6;
   }
   .line-numbers {
      padding: 10px 7.5px;
      background: #f0f0f0;
      color: #888;
      text-align: right;
      user-select: none;
      font-family: "Fira code", "Fira Mono", monospace;
      font-size: 15px;
      line-height: 1.5;
      border-right: 1px solid #ddd;
   }
   .editor textarea, .editor pre {
      tab-size: 2;
      -moz-tab-size: 2;
      -o-tab-size: 2;
      margin: 0;
      white-space: pre-wrap;
      word-wrap: break-word;
   }
   .editor pre, .editor code, .editor span {
      font-family: "Fira code", monospace !important;
      font-size: 15px !important;
      line-height: 1.5 !important;
   }
`;

// ---
function getExampleCode(lang: 'javascript' | 'python'): string {
   if (lang === 'javascript') {
      return `// If input is an array of objects
for (const item of $input) {
   item.myNewField = 1; // Add new field
}
return $input; // Output`;
   } else {
      return `# If input is a list of dictionaries
for item in _input:  
   item["myNewField"] = 1  # Add new field

return _input  # Output`;
   }
}